/**
 * Control Inheritance — Schema Fetch + Cache
 *
 * Module-level cache: fetched schemas persist for the page lifetime.
 * Concurrent requests for the same form ID are de-duplicated via a
 * promise map so the API is called exactly once per form per session.
 *
 * TTL / staleness: there is no automatic expiry. If a base form is updated
 * in the database, cached consumers will see the old version until the page
 * is reloaded or clearInheritanceCache() is called explicitly.
 * Call clearInheritanceCache() on user logout or when navigating to a fresh
 * form context to avoid serving stale base-form definitions.
 */

import { FormDefinitionApiClient, getAtlasApiConfig } from '@atlas-forms/client-js';
import type { FormSchema } from '@atlas-forms/types-js';

const _schemas = new Map<number, FormSchema>();
const _inflight = new Map<number, Promise<FormSchema>>();

export async function fetchInheritedSchema(formId: number): Promise<FormSchema> {
  if (_schemas.has(formId)) return _schemas.get(formId)!;

  if (_inflight.has(formId)) return _inflight.get(formId)!;

  const promise = (async () => {
    const client = new FormDefinitionApiClient({ baseUrl: getAtlasApiConfig().baseUrl });
    const res = await client.getFormById(formId);

    if (!res.success || !res.data) {
      throw new Error(`Inherited form ${formId} not found`);
    }

    const raw: unknown = res.data.Schema;
    const schema: FormSchema =
      typeof raw === 'string' ? (JSON.parse(raw) as FormSchema) : (raw as FormSchema);

    _schemas.set(formId, schema);
    _inflight.delete(formId);
    return schema;
  })();

  _inflight.set(formId, promise);
  promise.catch(() => _inflight.delete(formId));

  return promise;
}

/** Clear the entire cache — call on user logout or full context reset. */
export function clearInheritanceCache(): void {
  _schemas.clear();
  _inflight.clear();
}
